var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as Sentry from '@sentry/browser';
import LocalStorage, { STORAGE_KEYS } from '../services/localStorage';
import { getBrowserName, isWindows } from '../utils';
import { INTERNAL_AUDIO_MIC_ID, isInternalAudio } from '../utils/internalAudio';
import { trackMicPermission } from '../utils/segment';
export const AUDIO_CONSTRAINTS = {
    echoCancellation: true,
    noiseSuppression: false,
    autoGainControl: false,
};
// TODO: Have a better way to check if a bluetooth mic is being used
// Google Chrome will include the label for a bluetooth microphone, while other
// browsers do not. We use the this check to know when to tell the user to use
// a headset and providing extra information to the backend. There might be
// better and more reliable ways to tell if a device is bluetooth than this.
export const isBluetoothMic = (mic) => {
    return mic && mic.label.toLowerCase().includes('bluetooth');
};
export const getMicrophoneByIdIfExist = (availableMics, microphoneId) => {
    return microphoneId && availableMics.find((mic) => mic.value === microphoneId);
};
export const getMicrophoneFromLocalStorage = (availableMics) => {
    const selectedMicId = LocalStorage.get(STORAGE_KEYS.SELECTED_MIC_ID);
    return getMicrophoneByIdIfExist(availableMics, selectedMicId);
};
const isDeviceAudioInput = (device) => {
    return device.kind === 'audioinput';
};
const isDeviceIdDefault = (device) => {
    return device.deviceId === 'default';
};
// TODO: Have a way to declare a default mic if browser doesn't give the info
// Google Chrome will label a default mic for us, but other browsers might not.
// So, it would be good to always make sure to have one default audio device.
// This makes it so that we can switch between the Ava Mic and the default mic
// depending on group convo or solo convo.
const getDefaultMic = (devices) => {
    return devices.find((device) => isDeviceAudioInput(device) && isDeviceIdDefault(device));
};
export const getCurrentAvailableMics = (devices, isAvaMicForbidden, phraseTranslation) => {
    // The list of devices may contain duplicates for 'default' device. Google Chrome will
    // contain duplicates, but other browsers might not.
    //
    // For example, we might have:
    //
    // * Default - MacBook Pro Microphone (Built-in)
    // * MacBook Pro Microphone (Built-in)
    //
    // We want to only show one to the user, so the 'default' will be filtered out.
    const defaultMic = getDefaultMic(devices);
    const availableMics = devices
        .filter((device) => isDeviceAudioInput(device) && !isDeviceIdDefault(device) && (!isInternalAudio(device) || !isAvaMicForbidden))
        .map((device) => ({
        value: device.deviceId,
        label: device.label,
        default: defaultMic ? device.groupId === defaultMic.groupId : false,
    }));
    const isFirefox = getBrowserName() === 'Firefox';
    const isAvaMicAvailableOnWindows = isWindows && !isFirefox && !isAvaMicForbidden && navigator.mediaDevices.getDisplayMedia;
    if (isAvaMicAvailableOnWindows) {
        // Capturing system-wide audio is currently only available on Windows, but not in
        // Firefox (see this issue: https://bugzilla.mozilla.org/show_bug.cgi?id=1541425).
        availableMics.unshift({
            value: INTERNAL_AUDIO_MIC_ID,
            label: 'Ava Computer Audio',
            default: false,
        });
    }
    if (!availableMics.length) {
        availableMics.push({
            value: 'Missing',
            label: phraseTranslation('micSettings.noMic'),
            default: false,
        });
    }
    return availableMics;
};
export const isPermissionDenied = (err) => {
    const chromePermissionDenied = err.message.includes('Permission denied');
    const firefoxSafariPermissionDenied = err.message.includes('not allowed by the user agent');
    return chromePermissionDenied || firefoxSafariPermissionDenied;
};
export const isPermissionDismissed = (err) => {
    return err.message.includes('Permission dismissed');
};
export const hasNoAudioInput = (err) => {
    // We treat unavailable mic(s) the same as if access is denied.
    return err.message.includes('no audio input');
};
export const hasNoAudioTracks = (err) => {
    // Relates to Windows users, where the UX is a bit confusing:
    // they have to allow desktop sharing, but also check the checkbox for audio,
    // which is not very clear and some users miss it.
    return err.message.includes('no audio input');
};
export const isMicAccessDenied = (err) => {
    return isPermissionDenied(err) || isPermissionDismissed(err) || hasNoAudioInput(err) || hasNoAudioTracks(err);
};
export const trackMicPermissionGrantedIfPromptExists = (trackMicPermissionPrompt) => {
    if (trackMicPermissionPrompt) {
        trackMicPermission('granted');
    }
};
export const trackMicPermissionIfPromptExists = (err, trackMicPermissionPrompt) => {
    if (trackMicPermissionPrompt) {
        if (isPermissionDenied(err)) {
            trackMicPermission('denied');
        }
        else {
            trackMicPermission('undetermined');
        }
    }
};
export const captureMicrophoneExceptionIfExists = (err, t, errorShown, setErrorShown, callback) => {
    const captureError = !isPermissionDenied(err) && !isPermissionDismissed(err) && !hasNoAudioInput(err);
    if (captureError) {
        if (!hasNoAudioTracks(err)) {
            Sentry.captureException(err);
        }
        let errorMessage;
        if (err.message.includes('no audio tracks')) {
            errorMessage = t('snackbar.request.shareAudioNeeded');
        }
        else {
            errorMessage = t('snackbar.request.microphoneMissing');
        }
        if (errorMessage && !errorShown) {
            setErrorShown(true);
            callback(errorMessage);
        }
    }
};
export const getWindowsAudioStream = () => __awaiter(void 0, void 0, void 0, function* () {
    let audioStream;
    const { mediaDevices } = window.navigator;
    if (window.isElectron) {
        audioStream = yield mediaDevices.getUserMedia({
            audio: {
                mandatory: { chromeMediaSource: 'desktop' },
            },
            video: {
                mandatory: { chromeMediaSource: 'desktop' },
            },
        });
    }
    else {
        audioStream = yield mediaDevices.getDisplayMedia({
            audio: AUDIO_CONSTRAINTS,
            // Even though we do not need video, 'Audio only requests are not
            // supported' is the error given by Windows.
            video: true,
        });
        if (!audioStream.getAudioTracks().length) {
            throw new Error('no audio tracks included');
        }
    }
    return audioStream;
});
export const stopAudioProcessor = (audioProcessor) => {
    if (audioProcessor) {
        audioProcessor.stop();
    }
};
export const stopStreams = (streams) => {
    if (streams) {
        streams.forEach((stream) => {
            stream.getTracks().forEach((track) => track.stop());
        });
    }
};
export const disconnectSources = (sources) => {
    if (sources) {
        sources.forEach((source) => source.disconnect());
    }
};
export const closeAudioContext = (audioContext) => __awaiter(void 0, void 0, void 0, function* () {
    if (audioContext && audioContext.state !== 'closed') {
        try {
            yield audioContext.close();
        }
        catch (err) {
            Sentry.captureException(err);
        }
    }
});
export const clearVolumeInterval = (volumeInterval) => {
    if (volumeInterval) {
        clearInterval(volumeInterval);
    }
};
